// ─────────────────────────────────────────────────────────────────────────────
// MerchQrCard, right-rail fan-signup QR for the merch_manager seat. Ports the
// legacy dashboard-wiring.js paintMerchQr + copy/print delegate (~3387-3490).
//
// Signup URL = https://artistcntrl.com/<band-slug>/signup (slug = band name
// lowercased, non-alnum → '-', trimmed). QR image comes from api.qrserver.com
// (600px inline, 900px for print). Copy flips to "Copied ✓" ~1.4s; Print opens
// a popup with the band name + a 900px QR for clean output. Empty state prompts
// to set the band name when there is none.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type JSX } from 'react';
import type { User } from '../../lib';
import { copyText } from '../../lib';
import { bandSlug } from '../../lib/contacts';

export interface MerchQrCardProps {
  readonly user: User | null;
}

function signupUrl(bandName: string | null | undefined): string {
  const slug = bandSlug(String(bandName || ''));
  return slug ? 'https://artistcntrl.com/' + slug + '/signup' : '';
}

function qrSrc(url: string, size: number, margin: number): string {
  return (
    'https://api.qrserver.com/v1/create-qr-code/?size=' +
    size +
    'x' +
    size +
    '&margin=' +
    margin +
    '&data=' +
    encodeURIComponent(url)
  );
}

/** Escape for the print-window document string (band name + url are user data). */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function MerchQrCard({ user }: MerchQrCardProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const bandName = user?.band_name || '';
  const url = signupUrl(bandName);

  function onCopy(): void {
    if (!url) return;
    void copyText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    });
  }

  function onPrint(): void {
    if (!url) return;
    const printName = bandName || 'Sign up';
    const bigQr = qrSrc(url, 900, 12);
    const w = window.open('', '_blank', 'width=720,height=900');
    if (!w) {
      window.alert('Allow pop-ups to print.');
      return;
    }
    w.document.write(
      '<!doctype html><html><head><title>' +
        esc(printName) +
        ', sign up</title>' +
        '<style>body{font-family:system-ui,-apple-system,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:48px 24px;text-align:center;color:#0e0c0b;}' +
        'h1{font-size:48px;margin:0 0 8px;letter-spacing:-0.02em;}' +
        'p{margin:0 0 28px;color:#666;font-size:18px;}' +
        'img{width:520px;height:520px;max-width:80vw;}' +
        '.url{margin-top:20px;font-size:13px;color:#888;}' +
        '@media print{p,h1{margin-bottom:14px}}' +
        '</style></head><body>' +
        '<h1>' +
        esc(printName) +
        '</h1>' +
        '<p>Scan to sign up for shows + drops</p>' +
        '<img src="' +
        bigQr +
        '" alt="QR" />' +
        '<p class="url">' +
        esc(url) +
        '</p>' +
        '<scr' +
        'ipt>window.addEventListener("load",function(){setTimeout(function(){window.print();},300);});</scr' +
        'ipt>' +
        '</body></html>'
    );
    w.document.close();
  }

  return (
    <section className="card merch-qr-card" id="merch-qr-card">
      <header className="card__head">
        <h3 className="card__title">Merch QR</h3>
      </header>
      <div className="card__body merch-qr-card__body">
        {!url ? (
          // ── EMPTY: no band name yet ──
          <p className="empty-note">
            Set band name
            <br />
            <span>Settings → Profile</span>
          </p>
        ) : (
          <>
            <a
              className="merch-qr-card__frame"
              href={qrSrc(url, 600, 8)}
              target="_blank"
              rel="noreferrer"
            >
              <img className="merch-qr-card__img" src={qrSrc(url, 600, 8)} alt="Fan signup QR" />
            </a>
            <p className="merch-qr-card__name">{bandName || 'Artist CNTRL'}</p>
            <p className="merch-qr-card__url">{url.replace(/^https?:\/\//, '')}</p>
            <div className="merch-qr-card__actions">
              <button
                type="button"
                className={
                  'merch-qr-card__btn' + (copied ? ' merch-qr-card__btn--ok' : '')
                }
                onClick={onCopy}
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
              <button type="button" className="merch-qr-card__btn" onClick={onPrint}>
                Print
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
